import classnames from 'classnames/bind';
import { WIDGET_REFETCH_INTERVAL } from 'dashboards/services/interval';
import { DataWidget } from 'types/dashboard_widget';

import { createCsvFile } from 'services/csv';
import { downloadFile } from 'services/download_file';
import { getDimensionsFromMetricsResponse } from 'dashboards/services/metric';
import { rowsToTimezone } from 'dashboards/services/table';
import { sanitizeDataWidgetFilters } from 'dashboards/components/DataWidgetFiltersInput/sanitize';
import { useLeaderboard } from 'dashboards/hooks/useLeaderboard';
import { useTimeRangeParams } from 'dashboards/hooks/useTimeRangeParams';
import { useTopLevelDataWidgetFilters } from 'dashboards/hooks/useTopLevelDataWidgetFilters';
import { useUserMetrics } from 'dashboards/hooks/useUserMetrics';

import { ChartError } from 'dashboards/components/ChartError/ChartError';
import { ChartLoader } from 'dashboards/components/ChartLoader/ChartLoader';
import { Icon } from 'components/Icon/IconDeprecated';
import { Table } from 'dashboards/components/Table/Table';
import { Widget } from 'dashboards/widgets/Widget/Widget';

import styles from './Chart.module.scss';

const c = classnames.bind(styles);

export type LeaderboardTableProps = {
  widget: DataWidget<'leaderboard-table'>;
};

/** Component for {@link ChartType} leaderboard-table */
export function LeaderboardTable({ widget }: LeaderboardTableProps) {
  const { metric, ...query } = widget.query;

  const { start, stop, timezone } = useTimeRangeParams();

  const { data: userMetrics } = useUserMetrics();

  const columns = getDimensionsFromMetricsResponse(metric, userMetrics);

  const filters = useTopLevelDataWidgetFilters({
    filters: sanitizeDataWidgetFilters(widget.query.filters),
    columns,
  });

  const {
    data,
    isLoading: isLoadingLeaderboard,
    error,
    isPreviousData,
    isFetching,
  } = useLeaderboard({
    params: {
      metricName: metric,
      timeRange: { start, stop },
      timeZone: timezone,
      ...query,
      filters,
    },
    refetchInterval: WIDGET_REFETCH_INTERVAL,
    keepPreviousData: true,
  });

  const isLoading = isLoadingLeaderboard || (isFetching && isPreviousData);

  if (error) {
    return (
      <Widget widget={widget}>
        <ChartError />
      </Widget>
    );
  }

  if (!data && isLoading) {
    return (
      <Widget widget={widget}>
        <ChartLoader />
      </Widget>
    );
  }

  if (!data) {
    return null;
  }

  const { headers, rows } = data;

  return (
    <Widget
      widget={widget}
      menuItems={{
        csv: {
          children: 'Export as CSV',
          leftSection: <Icon name="export" size="small" />,
          onClick: () =>
            downloadFile(
              widget.name,
              createCsvFile(rowsToTimezone({ headers, rows, timezone }))
            ),
          disabled: rows.length === 0,
        },
      }}
    >
      <div className={c('chart')}>
        <Table headers={headers} rows={rows} isLoading={isLoading} />
      </div>
    </Widget>
  );
}
